import { useRef } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  UpdateUserSettingsRequest,
  UpdateUserSettingsResponse,
} from '@pkg/types/api'

import api from '@/api'

const USER_SETTINGS_KEY = 'userSettings'

export const useUserSettings = () => {
  return useQuery({
    queryKey: [USER_SETTINGS_KEY],
    queryFn: async () => (await api.getUserSettings()).data.userSettings,
  })
}

export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateUserSettingsRequest) =>
      api.updateUserSettings(data),

    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [USER_SETTINGS_KEY] })
      const previousUserSettings = queryClient.getQueryData([USER_SETTINGS_KEY])
      queryClient.setQueryData(
        [USER_SETTINGS_KEY],
        (oldData: UpdateUserSettingsResponse['userSettings']) => ({
          ...oldData,
          ...data,
        }),
      )
      return { previousUserSettings }
    },
    onError: (_err, _data, context) => {
      if (context?.previousUserSettings) {
        queryClient.setQueryData(
          [USER_SETTINGS_KEY],
          context.previousUserSettings,
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SETTINGS_KEY] })
    },
  })
}
export const useSendTestOptoutEmail = () => {
  // don't send queries more than once in 10 seconds
  const DELAY = 1000 * 10 // 10 seconds in milliseconds

  // Use a ref to store the timestamp of the last email sent
  const lastEmailSentAtRef = useRef<number>(0)

  return useMutation({
    mutationFn: () => {
      const now = Date.now()
      if (now - lastEmailSentAtRef.current < DELAY) {
        return Promise.resolve(undefined)
      }
      lastEmailSentAtRef.current = now
      return api.sendTestOptoutEmail()
    },
  })
}
